import { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useMatch, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Spinner from "../../components/BootstrapCompo";
import { GlobalContext, handleError } from "../../context/Provider";
import henceforthApi from "../../utils/henceforthApi";

const EditBrands = () => {
    const match = useMatch("brands/:id/edit");
    const location = useLocation();
    const urlSearchParams = new URLSearchParams(location.search);
    const { loading, setLoading, authState } = useContext(GlobalContext);
    const navigate = useNavigate();

    const [state, setState] = useState({
        name: urlSearchParams.has("name") ? urlSearchParams.get("name") : "",
        category_id: [] as string[],
    });

    const [category, setCategory] = useState<any>({ data: [] });
    const [categoryIds, setCategoryIds] = useState<string[]>([]);
    const [categoryError, setCategoryError] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [error, setError] = useState({ name: "" });

    // Fetch categories for dropdown
    const fetchCategories = async () => {
        try {
            const res = await henceforthApi.Category.listCategory();
            setCategory(res?.data || { data: [] });
        } catch {
            console.log("error fetching categories");
        }
    };

    // Fetch brand details
    const initialise = async () => {
        setLoading(true);
        try {
            const apiRes = await henceforthApi.Brands.getDetail(match?.params.id);
            const data = apiRes.data;
            setState({
                name: data.name || "",
                category_id: data.category_id || [],
            });
            setCategoryIds(data.category_id || []);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
        initialise();
    }, [match?.params.id, authState.lang]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        if (name === "name") setError({ ...error, name: "" });
        setState({ ...state, [name]: value });
    };

    const toggleCategory = (id: string) => {
        setCategoryError("");
        setCategoryIds((prev) =>
            prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
        );
    };

    const onSubmit = async (e: any) => {
        e.preventDefault();

        let hasError = false;
        if (!state.name) {
            setError({ name: "Please enter name" });
            hasError = true;
        }
        if (!categoryIds.length) {
            setCategoryError("Please select at least one category");
            hasError = true;
        }
        if (hasError) return;

        setLoading(true);
        const payload = {
            _id: match?.params.id,
            name: state.name,
            language: authState.lang,
            category_id: categoryIds,
        };
        try {
            await henceforthApi.Brands.edit(payload);
            toast.success("Brand updated successfully");
            navigate("/brands/1");
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const selectedNames = category?.data
        ?.filter((item: any) => categoryIds.includes(item._id))
        ?.map((item: any) => item.name)
        ?.join(", ");

    return (
        <>
            <section className="breadcrum-box">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-12">
                            <h2 className="fw-semibold">Edit Brands</h2>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb m-0">
                                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/brands/1">Brands</Link></li>
                                    <li className="breadcrumb-item active fw-bold">Edit Brands</li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <div className='page-spacing'>
                <section className='product-detail'>
                    <div className="container-fluid">
                        <div className="row justify-content-center">
                            <div className="col-sm-10 col-md-6 col-lg-5 col-xl-4 col-xxl-3">
                                <div className="common-card">
                                    <div className="common-card-title">
                                        <h5>Edit Brands</h5>
                                    </div>
                                    <div className="common-card-content">
                                        <form onSubmit={onSubmit}>
                                            {/* Category Multi-Select Dropdown */}
                                            <div className="mb-3" ref={dropdownRef} style={{ position: "relative" }}>
                                                <label className="fw-bold">Select Categories</label>
                                                <div
                                                    className={`form-select ${categoryError ? "is-invalid" : ""}`}
                                                    role="button"
                                                    onClick={() => setDropdownOpen((o) => !o)}
                                                    style={{ cursor: "pointer", userSelect: "none", fontSize: "13px" }}
                                                >
                                                    {selectedNames || <span className="text-muted">Select categories</span>}
                                                </div>

                                                {dropdownOpen && (
                                                    <div
                                                        className="border rounded bg-white shadow-sm"
                                                        style={{
                                                            position: "absolute",
                                                            zIndex: 10,
                                                            width: "100%",
                                                            maxHeight: "220px",
                                                            overflowY: "auto",
                                                            marginTop: "2px"
                                                        }}
                                                    >
                                                        {Array.isArray(category?.data) && category.data.length ? (
                                                            category.data.map((item: any) => (
                                                                <label
                                                                    key={item._id}
                                                                    className="d-flex align-items-center gap-2 px-3 py-2 mb-0"
                                                                    style={{ cursor: "pointer" }}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input mt-0"
                                                                        checked={categoryIds.includes(item._id)}
                                                                        onChange={() => toggleCategory(item._id)}
                                                                    />
                                                                    <span>{item.name}</span>
                                                                </label>
                                                            ))
                                                        ) : (
                                                            <div className="px-3 py-2 text-muted">No categories available</div>
                                                        )}
                                                    </div>
                                                )}

                                                <div className={`${categoryError ? "invalid-feedback d-block" : ""}`}>
                                                    {categoryError}
                                                </div>
                                            </div>

                                            {/* Brand Name Input */}
                                            <div className="form-fields-box mb-4 is-invalid">
                                                <label className="mb-1 fw-bold">Brand Name:</label>
                                                <input
                                                    type="text"
                                                    placeholder="Brand Name"
                                                    className={`form-control ${error.name ? "is-invalid" : ""} rounded-0`}
                                                    value={state.name || ''}
                                                    name="name"
                                                    onChange={handleChange}
                                                    maxLength={55}
                                                />
                                                <div className={`${error.name ? "invalid-feedback" : ""}`}>
                                                    {error.name}
                                                </div>
                                            </div>

                                            <div className="button-box">
                                                <button type="submit" className="btn btn-theme w-100" disabled={loading}>
                                                    {loading ? <Spinner /> : "Update Brand"}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default EditBrands;